/**
 * ShopView — UI Phaser de la tienda de cartas entre oleadas.
 *
 * Muestra 3 ofertas (marco + estrellas por rareza, nombre, descripción y
 * botón COMPRAR con precio o GRATIS) más los botones REFRESH y LLEVAR TODO.
 * Toda la economía (costos, azar, aplicar efectos) vive en `ShopLogic` y
 * `CardEngine` (lógica pura, sin Phaser); este archivo solo dibuja y
 * reacciona a toques, y notifica el resultado por el bus de eventos y por
 * `onRunStateChange`.
 *
 * Contrato con quien la instancia (GameScene, en F2):
 * - al comprar 1 carta o LLEVAR TODO, emite `shop:purchased` (una vez por
 *   carta aplicada) y luego `shop:closed`, y se desmonta sola.
 * - antes de cada emisión reporta el RunState actualizado vía
 *   `onRunStateChange`, para que quien la use pueda sincronizar su copia.
 */

import Phaser from 'phaser';
import { gameEvents } from '../../core/events';
import type { Rng } from '../../core/rng';
import type { CardDef, CardOffer, RunState } from '../../core/types';
import { CardEngine } from '../../cards/CardEngine';
import {
  computeOfferCost,
  generateOffers,
  purchaseAllOffers,
  purchaseOffer,
  refreshOffers,
  resolveCard,
  SHOP_REFRESH_COST,
  totalCost,
} from '../../cards/ShopLogic';
import { RARITY_FRAME_COLOR, rarityStars, rarityTier } from '../../cards/rarity';
import { TEX } from '../../core/TextureFactory';

export interface ShopViewConfig {
  scene: Phaser.Scene;
  /** Centro horizontal del panel de tienda. */
  x: number;
  /** Centro vertical del panel de tienda. */
  y: number;
  width: number;
  height: number;
  /** RunState vigente al abrir la tienda (solo se lee `balls` para afordabilidad). */
  run: RunState;
  /** Pool completo de cartas disponibles (normalmente `src/data/cards.json`). */
  cardsPool: CardDef[];
  /** Oleada actual (para el sesgo de rareza y el costo escalado). */
  wave: number;
  /** Número de capítulo actual (1-indexado). */
  chapterNumber: number;
  /** Generador determinista compartido con el resto del run. */
  rng: Rng;
  /** Se llama cada vez que una compra/refresh cambia el RunState. */
  onRunStateChange: (next: RunState) => void;
}

const PANEL_BG = 0x1b1030;
const PANEL_BORDER = 0x453a70;
const CARD_BG = 0x241c3d;
const TEXT_LIGHT = '#f4f0ff';
const TEXT_MUTED = '#a99fd1';

/** Componente de tienda: se instancia al entrar en la fase SHOPPING y se autodestruye al comprar. */
export class ShopView {
  private readonly scene: Phaser.Scene;
  private readonly config: ShopViewConfig;
  private readonly container: Phaser.GameObjects.Container;
  private cardLayer: Phaser.GameObjects.Container;
  private run: RunState;
  private offers: CardOffer[];
  private refreshBtn?: Phaser.GameObjects.Container;
  private buyAllBtn?: Phaser.GameObjects.Container;
  private closed = false;

  constructor(config: ShopViewConfig) {
    this.scene = config.scene;
    this.config = config;
    this.run = config.run;

    this.offers = generateOffers({
      pool: config.cardsPool,
      wave: config.wave,
      chapterNumber: config.chapterNumber,
      rng: config.rng,
    });
    gameEvents.emit('shop:open', { offers: this.offers });

    this.container = this.scene.add.container(config.x, config.y);
    this.cardLayer = this.scene.add.container(0, 0);
    this.container.add(this.cardLayer);

    this.buildChrome();
    this.renderOffers();
    this.playEntranceAnimation();
  }

  /** Título y botones fijos (REFRESH / LLEVAR TODO) que no cambian de posición al refrescar. */
  private buildChrome(): void {
    const { width, height } = this.config;

    const bg = this.scene.add.graphics();
    bg.fillStyle(PANEL_BG, 0.96);
    bg.fillRoundedRect(-width / 2, -height / 2, width, height, 14);
    bg.lineStyle(2, PANEL_BORDER, 1);
    bg.strokeRoundedRect(-width / 2, -height / 2, width, height, 14);
    // bloquea toques hacia lo que haya detrás de la tienda
    bg.setInteractive(
      new Phaser.Geom.Rectangle(-width / 2, -height / 2, width, height),
      Phaser.Geom.Rectangle.Contains
    );
    this.container.add(bg);

    const title = this.scene.add
      .text(0, -height / 2 + 20, 'ELIGE TU CARTA', {
        fontFamily: 'monospace',
        fontSize: '16px',
        color: TEXT_LIGHT,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    this.container.add(title);

    // "saltar" — permite cerrar la tienda sin comprar (evita quedar atascado
    // si no alcanzan las bolas para ninguna oferta ni para refrescar). Zona
    // táctil ≥44px (criterio de accesibilidad del plan) aunque el texto sea chico.
    const skip = this.scene.add
      .text(width / 2 - 12, -height / 2 + 20, 'saltar ✕', {
        fontFamily: 'monospace',
        fontSize: '12px',
        color: TEXT_MUTED,
      })
      .setOrigin(1, 0.5);
    const skipTouch = Math.max(44, skip.height + 20);
    const skipTouchW = Math.max(44, skip.width + 20);
    skip.setInteractive({
      useHandCursor: true,
      hitArea: new Phaser.Geom.Rectangle(skip.width - skipTouchW, (skip.height - skipTouch) / 2, skipTouchW, skipTouch),
      hitAreaCallback: Phaser.Geom.Rectangle.Contains,
    });
    skip.on('pointerdown', () => this.onSkip());
    skip.on('pointerover', () => skip.setColor(TEXT_LIGHT));
    skip.on('pointerout', () => skip.setColor(TEXT_MUTED));
    this.container.add(skip);

    const footerY = height / 2 - 34;
    this.refreshBtn = this.buildButton(
      -width / 4,
      footerY,
      width / 2 - 24,
      44,
      '',
      TEX.buttonBase,
      () => this.onRefresh()
    );
    this.buyAllBtn = this.buildButton(
      width / 4,
      footerY,
      width / 2 - 24,
      44,
      '',
      TEX.buttonPrimary,
      () => this.onBuyAll()
    );
    this.container.add([this.refreshBtn, this.buyAllBtn]);
    this.updateFooterLabels();
  }

  /** Redibuja los textos de REFRESH / LLEVAR TODO según bolas y ofertas actuales. */
  private updateFooterLabels(): void {
    const refreshAffordable = this.run.balls >= SHOP_REFRESH_COST;
    this.setButtonLabel(
      this.refreshBtn,
      `REFRESH  (${SHOP_REFRESH_COST})`,
      refreshAffordable
    );

    const total = totalCost(this.offers);
    const buyAllAffordable = total <= this.run.balls;
    this.setButtonLabel(
      this.buyAllBtn,
      total > 0 ? `LLEVAR TODO  (${total})` : 'LLEVAR TODO  (GRATIS)',
      buyAllAffordable
    );
  }

  private setButtonLabel(
    button: Phaser.GameObjects.Container | undefined,
    label: string,
    enabled: boolean
  ): void {
    if (!button) return;
    const text = button.getData('label') as Phaser.GameObjects.Text;
    text.setText(label);
    button.setAlpha(enabled ? 1 : 0.45);
    const hit = button.getData('hit') as Phaser.GameObjects.Rectangle;
    hit.disableInteractive();
    if (enabled) {
      hit.setInteractive({ useHandCursor: true });
    }
  }

  /** Botón genérico (imagen base + texto centrado + zona táctil ≥44px). */
  private buildButton(
    x: number,
    y: number,
    width: number,
    height: number,
    label: string,
    textureKey: string,
    onClick: () => void
  ): Phaser.GameObjects.Container {
    const btn = this.scene.add.container(x, y);
    const img = this.scene.add.image(0, 0, textureKey).setDisplaySize(width, Math.max(44, height));
    const text = this.scene.add
      .text(0, 0, label, {
        fontFamily: 'monospace',
        fontSize: '12px',
        color: textureKey === TEX.buttonPrimary ? '#241c3d' : TEXT_LIGHT,
        fontStyle: 'bold',
        align: 'center',
      })
      .setOrigin(0.5);
    const hit = this.scene.add
      .rectangle(0, 0, width, Math.max(44, height), 0x000000, 0)
      .setInteractive({ useHandCursor: true });
    hit.on('pointerdown', onClick);
    hit.on('pointerover', () => img.setAlpha(0.9));
    hit.on('pointerout', () => img.setAlpha(1));
    btn.add([img, text, hit]);
    btn.setData('label', text);
    btn.setData('hit', hit);
    return btn;
  }

  /** Dibuja (o redibuja tras un REFRESH) las 3 tarjetas de oferta. */
  private renderOffers(): void {
    this.cardLayer.removeAll(true);

    const count = this.offers.length;
    const cardWidth = Math.min(140, (this.config.width - 32) / Math.max(count, 1) - 12);
    const cardHeight = this.config.height - 140;
    const gap = 12;
    const totalWidth = count * cardWidth + (count - 1) * gap;
    const startX = -totalWidth / 2 + cardWidth / 2;
    const cardY = -14;

    this.offers.forEach((offer, index) => {
      const card = resolveCard(this.config.cardsPool, offer.cardId);
      const cardX = startX + index * (cardWidth + gap);
      const panel = this.buildCardPanel(card, offer, cardWidth, cardHeight);
      panel.setPosition(cardX, cardY);
      panel.setScale(0.001);
      panel.setAlpha(0);
      this.cardLayer.add(panel);

      // entrada escalonada: cada carta aparece un poco después que la anterior.
      this.scene.tweens.add({
        targets: panel,
        scale: 1,
        alpha: 1,
        duration: 260,
        delay: 90 + index * 90,
        ease: 'Back.Out',
      });
    });
  }

  private buildCardPanel(
    card: CardDef,
    offer: CardOffer,
    width: number,
    height: number
  ): Phaser.GameObjects.Container {
    const panel = this.scene.add.container(0, 0);
    const tier = rarityTier(card.rarity);
    const frameColor = RARITY_FRAME_COLOR[tier];

    const g = this.scene.add.graphics();
    g.fillStyle(CARD_BG, 1);
    g.fillRoundedRect(-width / 2, -height / 2, width, height, 10);
    g.lineStyle(3, frameColor, 1);
    g.strokeRoundedRect(-width / 2, -height / 2, width, height, 10);
    panel.add(g);

    const stars = this.scene.add
      .text(0, -height / 2 + 16, rarityStars(card.rarity), {
        fontFamily: 'monospace',
        fontSize: '12px',
        color: '#ffd166',
      })
      .setOrigin(0.5);
    panel.add(stars);

    const name = this.scene.add
      .text(0, -height / 2 + 36, card.name, {
        fontFamily: 'monospace',
        fontSize: '12px',
        color: TEXT_LIGHT,
        fontStyle: 'bold',
        align: 'center',
        wordWrap: { width: width - 12 },
      })
      .setOrigin(0.5, 0);
    panel.add(name);

    const desc = this.scene.add
      .text(0, -6, card.desc, {
        fontFamily: 'monospace',
        fontSize: '10px',
        color: TEXT_MUTED,
        align: 'center',
        wordWrap: { width: width - 14 },
      })
      .setOrigin(0.5, 0);
    panel.add(desc);

    const affordable = offer.cost <= this.run.balls;
    const priceLabel = offer.cost === 0 ? 'GRATIS' : `${offer.cost}`;
    const buyBtn = this.buildButton(
      0,
      height / 2 - 26,
      width - 12,
      36,
      `COMPRAR\n${priceLabel}`,
      TEX.buttonPrimary,
      () => this.onBuyOne(offer)
    );
    this.setButtonLabel(buyBtn, `COMPRAR  ${priceLabel}`, affordable);
    panel.add(buyBtn);

    this.attachCardHoverPress(panel, width, height);

    return panel;
  }

  /** Pequeño "levantamiento" al pasar el puntero y "presión" al tocar, sobre la carta completa. */
  private attachCardHoverPress(panel: Phaser.GameObjects.Container, width: number, height: number): void {
    panel.setInteractive({
      hitArea: new Phaser.Geom.Rectangle(-width / 2, -height / 2, width, height),
      hitAreaCallback: Phaser.Geom.Rectangle.Contains,
      useHandCursor: true,
    });
    const tweenTo = (scale: number, duration: number) => {
      this.scene.tweens.add({ targets: panel, scale, duration, ease: 'Sine.Out' });
    };
    panel.on('pointerover', () => tweenTo(1.03, 120));
    panel.on('pointerout', () => tweenTo(1, 120));
    panel.on('pointerdown', () => tweenTo(0.97, 80));
    panel.on('pointerup', () => tweenTo(1.03, 100));
  }

  private playEntranceAnimation(): void {
    this.container.setScale(0.85);
    this.container.setAlpha(0);
    this.scene.tweens.add({
      targets: this.container,
      scale: 1,
      alpha: 1,
      duration: 220,
      ease: 'Back.Out',
    });
  }

  // -- interacciones ---------------------------------------------------

  private onRefresh(): void {
    if (this.closed || this.run.balls < SHOP_REFRESH_COST) return;
    const { run, offers } = refreshOffers(this.run, {
      pool: this.config.cardsPool,
      wave: this.config.wave,
      chapterNumber: this.config.chapterNumber,
      rng: this.config.rng,
    });
    this.run = run;
    this.offers = offers;
    this.config.onRunStateChange(this.run);
    gameEvents.emit('shop:open', { offers: this.offers });
    this.renderOffers();
    this.updateFooterLabels();
    this.pulse(this.cardLayer);
  }

  /** Cierra la tienda sin comprar ninguna carta. */
  private onSkip(): void {
    if (this.closed) return;
    this.finishAndClose();
  }

  /** e2e/debug: compra la primera oferta afordable, o salta si no alcanza. Siempre cierra. */
  debugBuyFirst(): boolean {
    const offer = this.offers.find((o) => o.cost <= this.run.balls);
    if (!offer) {
      this.onSkip();
      return false;
    }
    this.onBuyOne(offer);
    return true;
  }

  private onBuyOne(offer: CardOffer): void {
    if (this.closed || offer.cost > this.run.balls) return;
    const { run } = purchaseOffer(this.run, offer, this.config.cardsPool);
    this.run = run;
    this.config.onRunStateChange(this.run);
    gameEvents.emit('shop:purchased', { cardId: offer.cardId });
    this.finishAndClose();
  }

  private onBuyAll(): void {
    if (this.closed) return;
    const total = totalCost(this.offers);
    if (total > this.run.balls) return;
    const { run } = purchaseAllOffers(this.run, this.offers, this.config.cardsPool);
    this.run = run;
    this.config.onRunStateChange(this.run);
    for (const offer of this.offers) {
      gameEvents.emit('shop:purchased', { cardId: offer.cardId });
    }
    this.finishAndClose();
  }

  private finishAndClose(): void {
    if (this.closed) return;
    this.closed = true;
    gameEvents.emit('shop:closed', {});
    this.scene.tweens.add({
      targets: this.container,
      alpha: 0,
      scale: 0.9,
      duration: 160,
      ease: 'Sine.In',
      onComplete: () => this.destroy(),
    });
  }

  private pulse(target: Phaser.GameObjects.Container): void {
    target.setScale(0.96);
    this.scene.tweens.add({ targets: target, scale: 1, duration: 140, ease: 'Back.Out' });
  }

  /** Destruye el árbol de GameObjects de la tienda. Idempotente. */
  destroy(): void {
    this.container.destroy(true);
  }
}

/**
 * Reexport de conveniencia: permite a quien integra (GameScene) aplicar el
 * efecto de una carta directamente si en algún flujo necesita hacerlo fuera
 * de ShopView (p. ej. debug/tests manuales), sin duplicar el import.
 */
export { CardEngine, computeOfferCost };
